import type {
  dealerSearchIncoming,
  dealerSearchOutgoing,
  dealerSearchOutgoingDownload,
  dealerSearchOutgoingHits,
  dealerSearchOutgoingHitsDownload,
  dealerSearchOutgoingInternal,
  dealerSearchOutgoingInternalDownload,
  dealerSearchOutgoingInternalHits,
  dealerSearchOutgoingInternalHitsDownload
} from "@dealer-center/ddc-pages-account-search-interface";
import { isDealerSearchOutgoingInternalHits } from "@dealer-center/ddc-pages-account-search-interface";

const doNotGenerateBlankDataThatIsMoreThanXRecords = 10000;

export const areTwoDealerSearchIncomingEqual = (
  a?: dealerSearchIncoming,
  b?: dealerSearchIncoming
): boolean => {
  return JSON.stringify(a) === JSON.stringify(b);
};

export const convertData = (
  data: dealerSearchOutgoingInternal | dealerSearchOutgoingInternalDownload,
  oldData: dealerSearchOutgoing | null,
  request?: dealerSearchIncoming
): dealerSearchOutgoing | dealerSearchOutgoingDownload | null => {
  if (!data) return null;
  const equal = areTwoDealerSearchIncomingEqual(request, oldData?.request);
  const existingData =
    equal && oldData ? oldData : generateEmptyData(data, request);

  const position = data.from;
  const returnValue: dealerSearchOutgoing | dealerSearchOutgoingDownload = {
    from: data.from,
    size: data.size,
    totalHits: data.totalHits,
    hits: convertHits(data.hits, position, existingData.hits),
    request
  };
  return returnValue;
};

const blank = "------";
const emptyHit: dealerSearchOutgoingHits = {
  id: blank,
  key: "",
  name: blank,
  url: blank,
  status: "INACTIVE",
  type: "CLIENT",
  verticalType: "AUTOMOTIVE_DEALERSHIP_GROUP",
  franchiseList: [],
  city: blank,
  country: blank,
  state: blank,
  created: blank,
  grantedResources: [],
  dealerCodes: []
};

export const convertHits = (
  hits:
    | dealerSearchOutgoingInternalHits[]
    | dealerSearchOutgoingInternalHitsDownload[],
  position: number,
  existingHits: dealerSearchOutgoingHits[] | dealerSearchOutgoingHitsDownload[]
): dealerSearchOutgoingHits[] | dealerSearchOutgoingHitsDownload[] => {
  return existingHits.map((existingHit, i) => {
    const recordIndex = i - position;
    const record = hits[recordIndex];
    if (record) {
      return convertARecord(record, i.toString());
    } else {
      return existingHit;
    }
  });
};
export const convertARecord = (
  hit:
    | dealerSearchOutgoingInternalHits
    | dealerSearchOutgoingInternalHitsDownload,
  index: string
) => {
  if (isDealerSearchOutgoingInternalHits(hit)) {
    const returnValue: dealerSearchOutgoingHits = {
      id: hit.id,
      key: index,
      name: hit.name,
      url: hit.url,
      status: hit.status,
      city: hit.address.city,
      country: hit.address.country,
      state: hit.address.state,
      created: new Date(hit.created).toDateString(),
      type: hit.type,
      verticalType: hit.verticalType,
      franchiseList: hit.franchiseList?.sort(),
      grantedResources: hit.grantedResources?.sort(),
      dealerCodes: hit.dealerCodes
    };
    return returnValue;
  } else {
    const returnValue: dealerSearchOutgoingHitsDownload = {
      id: hit.id,
      key: index,
      name: hit.name,
      url: hit.url,
      status: hit.status,
      city: hit.address.city,
      state: hit.address.state,
      verticalType: hit.verticalType,
      franchiseList: hit.franchiseList?.sort(),
      dealerCodes: hit.dealerCodes
    };
    return returnValue;
  }
};

export const generateEmptyData = (
  data: dealerSearchOutgoingInternal | dealerSearchOutgoingInternalDownload,
  request?: dealerSearchIncoming
): dealerSearchOutgoing => {
  const hitsToFill =
    data.totalHits > doNotGenerateBlankDataThatIsMoreThanXRecords
      ? doNotGenerateBlankDataThatIsMoreThanXRecords
      : data.totalHits;
  return {
    from: data.from,
    size: data.size,
    totalHits: data.totalHits,
    hits: new Array(hitsToFill).fill(emptyHit).map((hit, i) => {
      return {
        ...hit,
        key: i.toString()
      };
    }),
    request
  };
};

export const getSizeToUse = (size?: number) => {
  return size ? (size > 100 ? 100 : size) : 100;
};
export const getUrlToUse = (from: number, sizeToUse: number) => {
  return `/landing/nxlambda/accountSearch?from=${from}&size=${sizeToUse}`;
};

export const getBulkUrlToUse = () => {
  return `/landing/nxlambda/accountSearchAll`;
};
export const getSearchFilterUrlToUse = () => {
  return `/landing/nxlambda/accountSearchFilters`;
};

export const getOptions = (searchRequest: dealerSearchIncoming) => {
  return {
    method: "POST",
    body: JSON.stringify(searchRequest)
  };
};
